import type { Invitation, InvitableRole, ProjectRole } from "../types";

/**
 * The admin console's and projects' phase 2 records as the web app reads them (`crates/services/identity/src/routes`:
 * project_deletion, project_info, notifications, bulk_invite, teams, folder_grants, audit_export, exports, security).
 * They exist only against the identity service (VITE_IDENTITY_MODE=live); the demo has none of them.
 */

/** A project in Recently deleted (FR-PROJ-097). */
export interface DeletedProject {
  id: string;
  name: string;
  client: string;
  shortId: string;
  deletedAt: string;
  deletedBy: { id: string; name: string };
  purgeAfter: string;
  daysRemaining: number;
}

/** The organisation's recovery window and project numbering pattern (FR-ADMIN-070, 071; FR-PROJ-055). */
export interface ProjectRules {
  recoveryDays: number;
  numberPattern: string;
}

/** The project information record title blocks read (FR-PROJ-111). */
export interface ProjectInformation {
  projectNumber: string;
  name: string;
  client: string;
  ownerName: string | undefined;
  siteAddress: string | undefined;
  city: string;
  ward: string | undefined;
  state: string;
  plots: { name: string; identifiers: unknown }[];
  architectOfRecord: { name: string | undefined; coaNumber: string | undefined };
  issueDate: string | undefined;
  updatedAt: string | undefined;
  updatedBy: string | undefined;
}

/** The fields typed in Project information; the rest come from elsewhere. Empty clears a field. */
export interface ProjectInformationInput {
  ownerName: string;
  siteAddress: string;
  ward: string;
  architectName: string;
  coaNumber: string;
}

export const NOTIFICATION_KINDS = [
  "mention",
  "assignment",
  "approval",
  "comment",
  "transmittal",
  "security",
  "thread",
  "reply",
  "resolve",
  "reopen",
  "markup",
] as const;
export type NotificationKindSetting = (typeof NOTIFICATION_KINDS)[number];

/** One kind's channels (FR-PROJ-082; FR-COLLAB-152). */
export interface NotificationSetting {
  kind: NotificationKindSetting;
  inApp: boolean;
  email: boolean;
}

export interface NotificationSettings {
  settings: NotificationSetting[];
  /** Project settings only: whether the project has its own, rather than following the person's defaults. */
  own: boolean | undefined;
}

/** One entry in the person's in-app inbox. */
export interface InboxNotification {
  id: string;
  projectId: string | undefined;
  kind: string;
  title: string;
  body: string;
  link: string | undefined;
  actorId: string | undefined;
  createdAt: string;
  read: boolean;
}

export interface Inbox {
  notifications: InboxNotification[];
  unread: number;
}

/** What bulk invitations take (FR-ADMIN-026): rows typed in, a CSV, or pasted emails. */
export type BulkInviteInput =
  { csv: string; role?: InvitableRole } | { text: string; role?: InvitableRole };

export interface BulkInviteResult {
  sent: number;
  invitations: Invitation[];
  /** Rows refused, by number (the first data row is 1), with the reason. */
  refused: { row: number; email: string; reason: string }[];
}

/** A team (FR-ADMIN-011). */
export interface Team {
  id: string;
  name: string;
  handle: string;
  members: { userId: string; teamAdmin: boolean }[];
  projects: { projectId: string; role: string }[];
}

export const FOLDER_LEVELS = ["view", "view_download", "upload", "edit", "manage"] as const;
export type FolderLevel = (typeof FOLDER_LEVELS)[number];

/** A person's or a team's level on a folder (FR-ADMIN-114). */
export interface FolderGrant {
  kind: "person" | "team";
  id: string;
  level: FolderLevel;
}

/** What checking an audit hash chain found (FR-ADMIN-061). */
export interface AuditVerdict {
  ok: boolean;
  checked: number;
  unchained: number;
  brokenAt: string | undefined;
}

export type AuditExportFormat = "csv" | "jsonl";

export type ExportStatus = "queued" | "running" | "ready" | "failed";

/** How far a building export has gone: `done` of `total` parts, and the part being written. */
export interface ExportProgress {
  stage: string;
  done: number;
  total: number;
}

/** One finished ZIP64 of an export: the organisation's, or one project's (FR-IO-139). */
export interface ExportPart {
  name: string;
  bytes: number;
  sha256: string;
  project: string | undefined;
}

/** A whole-organisation export (FR-ADMIN-068, 069): built by the doc service in parts. */
export interface OrganisationExport {
  id: string;
  status: ExportStatus;
  requestedBy: string;
  createdAt: string;
  finishedAt: string | undefined;
  sizeBytes: number | undefined;
  expiresAt: string | undefined;
  error: string | undefined;
  progress?: ExportProgress | undefined;
  parts: ExportPart[];
}

/** A signed, time-limited link to one part (FR-ADMIN-069); each one made is in the audit log. */
export interface ExportLink {
  /** The bucket's presigned URL, or a path on the files API (`/files/…`) in development. */
  url: string;
  expiresAt: string;
  name: string;
}

/** A file the service sent back: an audit export or an organisation archive. */
export interface Download {
  blob: Blob;
  filename: string;
  /** `X-Orthos-Sha256`: the body's checksum. */
  sha256: string | undefined;
  /** `X-Orthos-Signature`: the audit export's HMAC, which verifying it needs. */
  signature: string | undefined;
}

/** The calls behind the phase 2 admin and project screens. `org` is the session's organisation ID. */
export interface ConsoleApi {
  listDeleted(org: string): Promise<DeletedProject[]>;
  deleteProject(projectId: string): Promise<void>;
  restoreProject(org: string, projectId: string): Promise<void>;
  deleteForever(org: string, projectId: string, confirm: string): Promise<void>;
  projectRules(org: string): Promise<ProjectRules>;
  setProjectRules(org: string, rules: ProjectRules): Promise<ProjectRules>;

  information(projectId: string): Promise<ProjectInformation>;
  saveInformation(projectId: string, input: ProjectInformationInput): Promise<ProjectInformation>;

  inbox(): Promise<Inbox>;
  readNotification(id: string): Promise<void>;
  readAllNotifications(): Promise<void>;
  notificationSettings(projectId?: string): Promise<NotificationSettings>;
  saveNotificationSettings(
    settings: NotificationSetting[],
    projectId?: string,
  ): Promise<NotificationSettings>;
  /** The project follows the person's defaults again. */
  clearProjectNotificationSettings(projectId: string): Promise<void>;

  bulkInvite(org: string, input: BulkInviteInput): Promise<BulkInviteResult>;

  teams(org: string): Promise<Team[]>;
  createTeam(org: string, name: string, handle?: string): Promise<Team>;
  renameTeam(org: string, team: string, name: string): Promise<Team>;
  deleteTeam(org: string, team: string): Promise<void>;
  setTeamMember(org: string, team: string, userId: string, teamAdmin: boolean): Promise<Team>;
  removeTeamMember(org: string, team: string, userId: string): Promise<void>;
  setTeamProjectRole(projectId: string, team: string, role: ProjectRole): Promise<Team>;
  removeTeamProjectRole(projectId: string, team: string): Promise<void>;

  folderGrants(projectId: string, folder: string): Promise<FolderGrant[]>;
  setFolderGrants(projectId: string, folder: string, grants: FolderGrant[]): Promise<FolderGrant[]>;

  exportAudit(org: string, format: AuditExportFormat, query: string): Promise<Download>;
  verifyAudit(org: string): Promise<AuditVerdict>;
  verifyAuditExport(org: string, body: string, signature: string): Promise<AuditVerdict>;

  exports(org: string): Promise<OrganisationExport[]>;
  startExport(org: string, projects: "all" | string[]): Promise<OrganisationExport>;
  /** A download link for one part, from the doc service, which asks identity whether the person may. */
  exportLink(exportId: string, part: string): Promise<ExportLink>;

  aiEnabled(org: string): Promise<boolean>;
  setAiEnabled(org: string, enabled: boolean): Promise<boolean>;
  signOutMember(org: string, userId: string): Promise<{ sessionsEnded: number }>;
  resetSecondFactor(
    org: string,
    userId: string,
  ): Promise<{ factorsRemoved: number; sessionsEnded: number }>;
}
